// Library Methods
import { useState } from "react";
import { useTranslation } from "react-i18next";

// Styles
import {
  getSurfaceTokens,
  ModalCustomConfirmTeal,
} from "src/styles/mui/muiStylesHelper";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import HubCard from "../HubCard/HubCard";
import AddEMRHubModal from "../AddEMRHubModal/AddEMRHubModal";
import { useTheme, Theme } from "@mui/material/styles";
import { PlusIcon, HeartbeatIcon } from "src/assets/icons";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Hooks
import { useEMRHubs } from "src/services/emr/hooks/emr-hubs/useEMRHubs";
import useEMRLiveUpdates from "src/services/emr/live/useEMRLiveUpdates";

const ACCENT_TEAL = "#1B8F76";

const getCardBackground = (theme: Theme) =>
  theme.palette.mode === "dark" ? "#00000090" : "rgba(255, 255, 255, 0.55)";

const EmrIntegration = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { onSurface } = getSurfaceTokens(theme);

  const { clinicInfo } = useClinicContext();

  useEMRLiveUpdates(clinicInfo?.id);

  const { data: emrHubs, isLoading: isLoadingHubs } = useEMRHubs(
    clinicInfo?.id
  );

  const [openAddHubModal, setOpenAddHubModal] = useState(false);

  return (
    <Box display="flex" width="100%" flexDirection="column" gap="16px">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          backgroundColor: getCardBackground(theme),
          borderRadius: "8px",
          padding: "16px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box display="flex" alignItems="center" gap="8px">
            <HeartbeatIcon style={{ color: ACCENT_TEAL }} />
            <Typography fontSize="16px" fontWeight={600} color={ACCENT_TEAL}>
              {t("Data Agents")}
            </Typography>
          </Box>

          <Button
            startIcon={<PlusIcon />}
            sx={ModalCustomConfirmTeal}
            disabled={isLoadingHubs}
            onClick={() => setOpenAddHubModal(true)}
          >
            {t("Add")}
          </Button>
        </Box>

        <Box display="flex" flexDirection="column" gap="12px">
          {isLoadingHubs && (
            <Typography fontSize="14px" color={onSurface}>
              {t("Loading...")}
            </Typography>
          )}
          {!isLoadingHubs && !emrHubs?.length && (
            <Typography fontSize="14px" color={onSurface}>
              {t("No EMR hubs found.")}
            </Typography>
          )}
          <Grid container spacing="8px">
            {emrHubs?.map((emrHub) => (
              <Grid item key={emrHub.hubId} xs={12} sm={6}>
                <HubCard emrHub={emrHub} />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      <AddEMRHubModal
        open={openAddHubModal}
        clinicId={clinicInfo?.id}
        onClose={() => setOpenAddHubModal(false)}
      />
    </Box>
  );
};

export default EmrIntegration;
